// src/pages/RoomListPage.jsx

import React, { useEffect, useState } from "react";
import { getAllRooms } from "../services/roomService";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Typography,
  Container,
  Grid,
  Button,
  CircularProgress,
  Box,
  Paper,
  Skeleton,
  Card,
  CardContent,
  Stack,
  useTheme
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import RoomCard from "../components/RoomCard";

// Room Card Skeleton Component
const RoomCardSkeleton = () => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        maxWidth: 390,
        minWidth: 320,
        height: "100%", // Match the real cards
        borderRadius: theme.shape.borderRadius * 1.5,
        border: `1px solid ${theme.palette.divider}`,
        p: { xs: 1, sm: 1.25 },
        pb: { xs: 1, sm: 1.25 },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Room Image Skeleton */}
        <Skeleton 
          variant="rectangular" 
          width="100%" 
          height={{ xs: 180, sm: 228 }}
          sx={{ 
            borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`
          }}
        />

        <CardContent sx={{ p: 0, pl: 1, width: "100%", flex: 1 }}>
          {/* Room Name Skeleton */}
          <Skeleton 
            variant="text" 
            width="70%" 
            height={32} 
            sx={{ mb: 2.5 }}
          />

          {/* Room Fields Skeleton */}
          <Stack spacing={1.5} sx={{ pl: 0.5, width: "100%", mb: 2.5 }}>
            {[1, 2, 3].map((_, index) => (
              <Stack key={index} direction="row" spacing={1.5} alignItems="center">
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" width="80%" height={20} />
              </Stack>
            ))}
          </Stack>

          {/* Button and Link Skeleton */}
          <Stack 
            direction="row"  
            alignItems="center" 
            justifyContent="space-between"
            sx={{
              flexWrap: 'wrap',
              gap: 1,
              minHeight: 43,
            }}
          >
            <Skeleton 
              variant="rectangular" 
              width={193} 
              height={43}
              sx={{ 
                borderRadius: theme.shape.borderRadius * 0.5,
                flex: '1 1 auto',
                minWidth: 140,
                maxWidth: 193
              }}
            />
            <Skeleton 
              variant="text" 
              width={80} 
              height={20}
              sx={{ flexShrink: 0 }}
            />
          </Stack>
        </CardContent>
      </Box>
    </Card>
  );
};

// Room List Page Skeleton Component
const RoomListPageSkeleton = () => {
  return (
    <Container sx={{ mt: 4 }}>
      {/* Page Title Skeleton */}
      <Skeleton 
        variant="text" 
        width="30%" 
        height={48} 
        sx={{ mb: 3 }}
      />

      {/* Room Cards Grid Skeleton */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 3,
          justifyContent: 'center',
        }}
      >
        {/* Show 6 skeleton cards */}
        {[1, 2, 3, 4, 5, 6].map((_, index) => (
          <RoomCardSkeleton key={index} />
        ))}
      </Box>
    </Container>
  );
};

function RoomListPage() {
  const [rooms, setRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  
  // Get user from Redux store
  const user = useSelector((state) => state.user.user);
  
  // Check if user is organization admin (same logic as Navbar)
  const isOrgAdmin = () => {
    if (!user || !user.currentOrganization || !user.currentOrganization.users) return false;
    const entry = user.currentOrganization.users.find(
      (e) => e.user && e.user._id && e.user._id === user._id
    );
    return entry ? entry.role === "admin" : false;
  };

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        setError("");
        
        // Fetch rooms
        const roomsData = await getAllRooms();
        console.log('Fetched rooms:', roomsData); // Debug log to see if images are included
        
        setRooms(roomsData);
      } catch (err) {
        console.error("Failed to load data:", err);
        setError("Failed to load rooms");
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, [user]);

  if (isLoading) {
    return <RoomListPageSkeleton />;
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Room List
        </Typography>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Room List
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 3,
          justifyContent: 'center',
        }}
      >
        {rooms.map((room) => (
          <RoomCard key={room._id} room={room} />
        ))}
      </Box>
      
      {rooms.length === 0 && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Paper sx={{ p: 4, maxWidth: 400, mx: 'auto' }}>
            <Box sx={{ mb: 3 }}>
              <AddIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                No Meeting Rooms Yet
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {isOrgAdmin() 
                  ? "Create your first meeting room to get started!"
                  : "Your organization doesn't have any meeting rooms set up yet."
                }
              </Typography>
            </Box>
            
            {isOrgAdmin() ? (
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/create-room"
                startIcon={<AddIcon />}
                sx={{ mt: 2 }}
              >
                Create First Room
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Contact your administrator to add rooms.
              </Typography>
            )}
          </Paper>
        </Box>
      )}
    </Container>
  );
}

export default RoomListPage;